import type { TFunction } from 'i18next';
import { z } from 'zod';

export type ContactValues = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
};

export type OrgFormValues = {
  name: string;
  address: string;
  alias: string;
  primaryContact: ContactValues;
  itContact: ContactValues;
  domain: string;
  subscription: {
    active: boolean;
    expiration: string;
  };
};

const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;
const EMAIL_FORMAT_SCHEMA = z.string().email();
const DOMAIN_REGEX = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
// Alias is used as a URL-safe slug — letters, numbers, and hyphens only.
const ALIAS_REGEX = /^[a-zA-Z0-9-]+$/;
// Person names — letters (incl. accented), spaces, hyphens, and apostrophes.
const PERSON_NAME_REGEX = /^[\p{L}][\p{L}\s'-]*$/u;

/** Today's date as a local `YYYY-MM-DD` string, matching the `CalendarDate.toString()` format the DatePicker stores. */
function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const NAME_MAX_LENGTH = 100;
const ALIAS_MAX_LENGTH = 50;
const ADDRESS_MAX_LENGTH = 500;
const PERSON_NAME_MAX_LENGTH = 50;

/** Adds a single "required" or "too long" issue for a free-text field, trimming before checking. Callers add any additional format checks after calling this since only one issue path is added here. */
function checkRequiredAndLength(
  ctx: z.RefinementCtx,
  value: string,
  path: (string | number)[],
  maxLength: number,
  messages: { required: string; tooLong: string },
): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: messages.required, path });
    return false;
  }
  if (trimmed.length > maxLength) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: messages.tooLong, path });
    return false;
  }
  return true;
}

// `originalExpiration` is the expiration already persisted for this org (edit forms only): a
// pre-existing lapsed subscription must stay editable for unrelated fields, or the past-date check
// would retroactively block every save on such an org.
export function createOrgSchema(
  t: TFunction<'orgs'>,
  existingAliases?: ReadonlySet<string>,
  options: { originalExpiration?: string } = {},
) {
  const contactSchema = z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      phone: z.string(),
      email: z.string(),
    })
    .superRefine((val, ctx) => {
      (['firstName', 'lastName'] as const).forEach(field => {
        const requiredKey = field === 'firstName' ? 'validation_first_name_required' : 'validation_last_name_required';
        const ok = checkRequiredAndLength(ctx, val[field], [field], PERSON_NAME_MAX_LENGTH, {
          required: t(requiredKey),
          tooLong: t('validation_person_name_max_length'),
        });
        if (ok && !PERSON_NAME_REGEX.test(val[field].trim())) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_person_name_invalid'), path: [field] });
        }
      });

      // Phone has no required indicator in the UI and the backend's `Contact` schema accepts it as
      // nullable, so its format is only checked when non-empty — a bare `.regex()` on the base string
      // type rejects '' outright and silently blocked every submission with an optional phone left blank.
      const phone = val.phone.trim();
      if (phone && !PHONE_REGEX.test(phone)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_phone_invalid'), path: ['phone'] });
      }

      // Backend's `Contact` schema requires `email` (unlike `phone`, which is nullable) — required
      // here too so a blank email fails fast client-side instead of a 422 after submit.
      const email = val.email.trim();
      if (!email) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_email_required'), path: ['email'] });
      } else if (!EMAIL_FORMAT_SCHEMA.safeParse(email).success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_email_invalid'), path: ['email'] });
      }
    });

  return z.object({
    // A leaf-level superRefine's ctx is already scoped to this field, so issue paths here are
    // relative (`[]`) — an explicit `['name']` would double up to `['name', 'name']` and the
    // error would never attach to the actual field.
    name: z.string().superRefine((val, ctx) => {
      const ok = checkRequiredAndLength(ctx, val, [], NAME_MAX_LENGTH, {
        required: t('validation_name_required'),
        tooLong: t('validation_name_max_length'),
      });
      if (ok && val.trim().length < 3) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_name_min_length'), path: [] });
      }
    }),
    address: z.string().superRefine((val, ctx) => {
      checkRequiredAndLength(ctx, val, [], ADDRESS_MAX_LENGTH, {
        required: t('validation_address_required'),
        tooLong: t('validation_address_max_length'),
      });
    }),
    alias: z.string().superRefine((val, ctx) => {
      const ok = checkRequiredAndLength(ctx, val, [], ALIAS_MAX_LENGTH, {
        required: t('validation_alias_required'),
        tooLong: t('validation_alias_max_length'),
      });
      if (!ok) return;
      if (val.trim().length < 2) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_alias_min_length'), path: [] });
      } else if (!ALIAS_REGEX.test(val.trim())) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_alias_invalid'), path: [] });
      } else if (existingAliases?.has(val.trim().toLowerCase())) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_alias_duplicate'), path: [] });
      }
    }),
    primaryContact: contactSchema,
    itContact: contactSchema,
    domain: z.string().min(1, t('validation_domain_required')).regex(DOMAIN_REGEX, t('validation_domain_invalid')),
    subscription: z
      .object({
        active: z.boolean(),
        expiration: z.string(),
      })
      .superRefine((val, ctx) => {
        if (!val.active) return;
        if (!val.expiration) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation_expiration_required'),
            path: ['expiration'],
          });
          return;
        }
        if (val.expiration === options.originalExpiration) return;
        if (val.expiration < getTodayDateString()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation_expiration_past'),
            path: ['expiration'],
          });
        }
      }),
  });
}
